import { CustomerInfoCard } from '@/components/orders';
import { PublicOrderAddress } from '@/components/orders/PublicOrderAddress';
import { PublicOrderError } from '@/components/orders/PublicOrderError';
import { PublicOrderHeader } from '@/components/orders/PublicOrderHeader';
import { PublicOrderItems } from '@/components/orders/PublicOrderItems';
import { PublicOrderLoading } from '@/components/orders/PublicOrderLoading';
import { PublicOrderPaidAlert } from '@/components/orders/public-order-paid-alert';
import { PublicOrderSummary } from '@/components/orders/PublicOrderSummary';
import { PublicSetLocationDialog } from '@/components/orders/PublicSetLocationDialog';
import { PublicStoreHeader } from '@/components/orders/PublicStoreHeader';
import { fetchEnableABA } from '@/api/setting-api';
import { verifyMessengerContext } from '@/api/fbapi';
import { MessengerSdkDebugPanel } from '@/components/orders/messenger-sdk-debug-panel';
import { usePublicOrderDetailsData } from '@/hooks/use-public-order-details-data';
import { usePublicOrderPayment } from '@/hooks/use-public-order-payment';
import { getApiUrl } from '@/lib/auth-utils';
import { routes } from '@/routes/route-constants';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'wouter';

const APP_ID = '822621300668517';

interface PublicOrderDetailPageProps {
  orderId?: string;
}

export default function PublicOrderDetailPage({ orderId }: PublicOrderDetailPageProps = {}) {
  const { id } = useParams();
  const effectiveOrderId = orderId ?? id;
  const [, setLocation] = useLocation();

  const { order, customer, orderItems, fulfillment, payments, isLoading, refresh, user } =
    usePublicOrderDetailsData(effectiveOrderId ?? null);

  const [isSetLocationOpen, setIsSetLocationOpen] = useState(false);
  const [enableABA, setEnableABA] = useState(false);

  const isLogin = !!user;
  const isPOS = order?.source === 'pos';
  const { isPaid, showPaidAlert } = usePublicOrderPayment({
    order,
    enableABA,
    refresh,
  });

  const updatedFulfillment = useMemo(() => {
    if (!fulfillment) {
      return {
        method: 'DELIVERY_CITY',
        carrier: 'VIREAK_BUNTHAM',
        price: '2.0',
      } as any;
    }
    const carrier = fulfillment.carrier;
    const isUnknown = !carrier || String(carrier) === 'UNKNOWN' || String(carrier) === 'unknown';
    return {
      ...fulfillment,
      carrier: isUnknown ? 'VIREAK_BUNTHAM' : carrier,
    };
  }, [fulfillment]);

  type StepStatus = 'idle' | 'loading' | 'ok' | 'error';
  type FbEnv = 'messenger' | 'facebook-app' | 'browser';
  type PsidSource = 'url-param' | 'sdk' | null;

  const [psid, setPsid] = useState<string | null>(null);
  const [psidSource, setPsidSource] = useState<PsidSource>(null);
  const [context, setContext] = useState<MessengerContext | null>(null);
  const [sdkStatus, setSdkStatus] = useState<StepStatus>('idle');
  const [contextStatus, setContextStatus] = useState<StepStatus>('idle');
  const [contextError, setContextError] = useState<string | null>(null);
  const [verifyStatus, setVerifyStatus] = useState<StepStatus>('idle');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [notInExtension, setNotInExtension] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [fbEnv] = useState<FbEnv>(() => {
    const ua = navigator.userAgent;
    if (ua.includes('Orca')) return 'messenger';
    if (ua.includes('FB4A') || ua.includes('FB_IAB')) return 'facebook-app';
    return 'browser';
  });

  useEffect(() => {
    if (isLoading || !order) return;

    const searchParams = new URLSearchParams(window.location.search);
    const dialog = searchParams.get('dialog');
    if (dialog === 'location' || dialog === 'set-location') {
      setIsSetLocationOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }

    fetchEnableABA()
      .then((res) => setEnableABA(res.data || false))
      .catch(() => setEnableABA(false));
  }, [isLoading, order]);

  useEffect(() => {
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 800;
    let attempt = 0;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const urlParams = new URLSearchParams(window.location.search);
    const psidFromUrl = urlParams.get('psid');
    if (psidFromUrl) {
      setPsid(psidFromUrl);
      setPsidSource('url-param');
    }

    const tryGetContext = () => {
      if (cancelled) return;
      if (!window.MessengerExtensions) return;

      setSdkStatus('ok');
      if (!window.MessengerExtensions.isInExtension()) {
        if (attempt < MAX_RETRIES) {
          attempt++;
          setRetryCount(attempt);
          setContextStatus('loading');
          setContextError(`isInExtension() = false, retrying… (${attempt}/${MAX_RETRIES})`);
          retryTimer = setTimeout(tryGetContext, RETRY_DELAY_MS);
        } else {
          setNotInExtension(true);
          setContextStatus('error');
          setContextError(`isInExtension() = false after ${MAX_RETRIES} retries.`);
        }
        return;
      }

      setContextStatus('loading');
      setContextError(null);
      window.MessengerExtensions.getContext(
        APP_ID,
        async (thread_context) => {
          if (cancelled) return;
          setContext(thread_context);
          if (thread_context.psid) {
            setPsid(thread_context.psid);
            setPsidSource('sdk');
          }
          setContextStatus('ok');
          if (thread_context.signed_request && effectiveOrderId) {
            setVerifyStatus('loading');
            try {
              const res = await verifyMessengerContext(
                thread_context.signed_request,
                effectiveOrderId,
              );
              if (res.success) setVerifyStatus('ok');
              else {
                setVerifyStatus('error');
                setVerifyError(res.message || 'Backend verification failed');
              }
            } catch (e: any) {
              setVerifyStatus('error');
              setVerifyError(String(e?.message ?? e));
            }
          }
        },
        (err: any) => {
          if (cancelled) return;
          const code = err?.code ?? err?.error_code ?? '?';
          const msg = err?.message ?? JSON.stringify(err);
          if (attempt < MAX_RETRIES) {
            attempt++;
            setRetryCount(attempt);
            setContextStatus('loading');
            setContextError(`Error ${code}: ${msg} — retrying (${attempt}/${MAX_RETRIES})…`);
            retryTimer = setTimeout(tryGetContext, RETRY_DELAY_MS);
          } else {
            setContextStatus('error');
            setContextError(`Error ${code}: ${msg} (gave up after ${MAX_RETRIES} retries)`);
          }
        },
      );
    };

    if (window.MessengerExtensions) {
      setSdkStatus('ok');
      tryGetContext();
    } else {
      setSdkStatus('loading');
      window.extAsyncInit = () => {
        if (!cancelled) tryGetContext();
      };
    }

    if (!document.getElementById('Messenger')) {
      const script = document.createElement('script');
      script.id = 'Messenger';
      script.src = 'https://connect.facebook.net/en_US/messenger.Extensions.js';
      script.async = true;
      script.onerror = () => {
        if (cancelled) return;
        setSdkStatus('error');
        setContextStatus('error');
        setContextError('Failed to load messenger.Extensions.js');
      };
      document.body.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <PublicOrderLoading />;
  if (!effectiveOrderId || !order) return <PublicOrderError error={null} noId={true} />;

  const showQr = !enableABA && !isPaid;

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4">
          <PublicStoreHeader compact />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        <PublicOrderPaidAlert open={showPaidAlert} />

        {false && isLogin && (
          <MessengerSdkDebugPanel
            appId={APP_ID}
            showDebug={showDebug}
            setShowDebug={setShowDebug}
            sdkStatus={sdkStatus}
            contextStatus={contextStatus}
            verifyStatus={verifyStatus}
            verifyError={verifyError}
            notInExtension={notInExtension}
            contextError={contextError}
            fbEnv={fbEnv}
            psid={psid}
            psidSource={psidSource}
            retryCount={retryCount}
          />
        )}

        <div
          className={
            showQr
              ? 'grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-4 items-start'
              : undefined
          }
        >
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <PublicOrderHeader
              orderNumber={order.orderNumber}
              isLogin={isLogin}
              id={effectiveOrderId}
              isPaid={isPaid}
            />
          </div>

          {showQr && (
            <div className="bg-white rounded-xl border border-slate-200 p-3">
              <img
                src={`${getApiUrl()}/qr/${order.id}`}
                alt="Pay with KHQR"
                className="w-full rounded-lg"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 items-start">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <PublicOrderItems
              order={order}
              orderItems={orderItems}
              isLogin={isLogin}
              orderStatus={order.status}
              onRefresh={() => refresh()}
            />
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <PublicOrderSummary order={order} orderItems={orderItems} />
            </div>

            {customer && !isPOS && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <PublicOrderAddress
                  payments={payments}
                  customer={customer}
                  order={order}
                  fulfillment={updatedFulfillment}
                />
              </div>
            )}

            {isLogin && customer && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <CustomerInfoCard
                  customer={customer}
                  onViewProfile={() => setLocation(routes.customer.detail(customer.id))}
                  onRefresh={() => refresh()}
                  orderId={order.id}
                  shippingAddress={order.shippingAddress || ''}
                  orderStatus={order.status}
                />
              </div>
            )}
          </div>
        </div>

        <div className="pt-2 pb-6 text-center space-y-0.5">
          <p className="text-slate-700 font-extrabold text-base leading-snug">
            សូមអរគុណសម្រាប់ការបញ្ជាទិញរបស់អ្នក!
          </p>
          <p className="text-slate-400 text-sm font-medium italic">Thank you for your order!</p>
        </div>
      </div>

      {customer && (
        <PublicSetLocationDialog
          open={isSetLocationOpen}
          onOpenChange={setIsSetLocationOpen}
          customerId={customer.id}
          currentAddress={order?.shippingAddress}
          onSuccess={() => refresh()}
          order={order}
        />
      )}
    </div>
  );
}
